import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaTimes, FaWallet, FaCheckCircle, FaExclamationTriangle, FaCopy, FaExternalLinkAlt } from 'react-icons/fa'
import { SiSolana } from 'react-icons/si'
import { useWallet } from '../contexts/WalletContext'
import { Button } from './ui/button'

export function WalletModal({ isOpen, onClose }) {
  const {
    walletType,
    account,
    balance,
    tokenBalance,
    stakedBalance,
    availableBalance,
    network,
    isConnecting,
    error,
    connectWallet,
    disconnectWallet,
    formatAddress,
    isConnected,
    tokenSymbol,
    tokenName
  } = useWallet()

  const [copied, setCopied] = React.useState(false)

  const copyAddress = () => {
    if (account) {
      navigator.clipboard.writeText(account)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const openExplorer = () => {
    if (account && walletType === 'solana') {
      window.open(`https://explorer.solana.com/address/${account}`, '_blank')
    }
  }

  const handleConnect = async () => {
    await connectWallet()
  }

  const handleDisconnect = () => {
    disconnectWallet()
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative z-10 w-full max-w-md mx-4"
        >
          <div className="bg-gradient-to-b from-gray-900 to-black border-2 border-electric-blue/30 rounded-2xl shadow-2xl shadow-electric-blue/20 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-electric-blue/10 to-tech-cyan/10 border-b border-electric-blue/20 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-electric-gradient rounded-full flex items-center justify-center">
                    {walletType === 'solana' ? (
                      <SiSolana className="text-black text-lg" />
                    ) : (
                      <FaWallet className="text-black text-lg" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-wider">
                      {isConnected ? 'Wallet Connected' : 'Connect Wallet'}
                    </h3>
                    {isConnected && network && (
                      <p className="text-xs text-electric-blue font-bold">
                        {network.name}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Not Connected State */}
              {!isConnected && (
                <div className="space-y-6">
                  {/* Security Notice */}
                  <div className="bg-electric-blue/5 border border-electric-blue/20 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <FaCheckCircle className="text-green-400 text-xl flex-shrink-0 mt-1" />
                      <div className="text-sm text-gray-300">
                        <p className="font-bold text-white mb-2">Secure Connection</p>
                        <ul className="space-y-1 text-xs">
                          <li>• Your private keys never leave your wallet</li>
                          <li>• We cannot access your funds without permission</li>
                          <li>• Always verify transaction details before signing</li>
                          <li>• Solana-powered for speed and low fees</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Error Display */}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <FaExclamationTriangle className="text-red-400 text-xl flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-red-400 mb-1">Connection Error</p>
                          <p className="text-sm text-gray-300">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Connect Button */}
                  <Button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="w-full bg-electric-gradient hover:shadow-electric text-lg font-black py-4"
                  >
                    {isConnecting ? (
                      <span className="flex items-center justify-center">
                        <div className="w-5 h-5 border-3 border-black border-t-transparent rounded-full animate-spin mr-3"></div>
                        Connecting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <SiSolana className="mr-3" />
                        Connect with Phantom
                      </span>
                    )}
                  </Button>

                  {/* Install Phantom Link */}
                  {!window.solana && (
                    <div className="text-center">
                      <p className="text-sm text-gray-400 mb-2">Don't have Phantom?</p>
                      <a
                        href="https://phantom.app/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-electric-blue hover:text-tech-cyan font-bold text-sm"
                      >
                        Install Phantom Wallet →
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Connected State */}
              {isConnected && (
                <div className="space-y-6">
                  {/* Account Info */}
                  <div className="bg-gradient-to-br from-electric-blue/5 to-tech-cyan/5 border border-electric-blue/20 rounded-xl p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Wallet Address</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-lg text-white font-bold">{formatAddress(account)}</p>
                      <div className="flex space-x-2">
                        <button
                          onClick={copyAddress}
                          className="p-2 bg-electric-blue/10 hover:bg-electric-blue/20 rounded-lg transition-colors"
                          title="Copy address"
                        >
                          <FaCopy className={`${copied ? 'text-green-400' : 'text-electric-blue'}`} />
                        </button>
                        <button
                          onClick={openExplorer}
                          className="p-2 bg-electric-blue/10 hover:bg-electric-blue/20 rounded-lg transition-colors"
                          title="View on Explorer"
                        >
                          <FaExternalLinkAlt className="text-electric-blue" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Balances */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* SOL Balance */}
                    <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <SiSolana className="text-purple-400" />
                        <p className="text-xs text-gray-400 uppercase tracking-wider">SOL</p>
                      </div>
                      <p className="text-2xl font-black text-white">{balance}</p>
                    </div>

                    {/* BODY Balance */}
                    <div className="bg-gradient-to-br from-neon-500/10 to-electric-500/10 border border-neon-400/30 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-4 h-4 bg-neon-400 rounded-full"></div>
                        <p className="text-xs text-neon-400 uppercase tracking-wider font-bold">{tokenSymbol}</p>
                      </div>
                      <p className="text-2xl font-black text-neon-400">{tokenBalance}</p>
                      <p className="text-xs text-gray-400 mt-1">{tokenName}</p>
                    </div>
                  </div>

                  {/* Balance Breakdown */}
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Token Balance Breakdown</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Total Balance:</span>
                        <span className="text-white font-bold">{tokenBalance} {tokenSymbol}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Currently Staked:</span>
                        <span className="text-amber-400 font-bold">{stakedBalance} {tokenSymbol}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-slate-700 pt-2">
                        <span className="text-gray-300 font-semibold">Available to Stake:</span>
                        <span className="text-electric-blue font-black">{availableBalance} {tokenSymbol}</span>
                      </div>
                    </div>
                  </div>

                  {/* Network Info */}
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <FaCheckCircle className="text-purple-400 text-lg flex-shrink-0 mt-1" />
                      <div className="text-sm">
                        <p className="font-bold text-purple-400 mb-1">Solana Network</p>
                        <p className="text-gray-300">
                          Connected to {network?.name}. Lightning-fast transactions with minimal fees (~$0.00025).
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Live Token Badge */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                    <div className="flex items-center justify-center space-x-2 text-sm">
                      <span className="text-green-400">✓</span>
                      <span className="text-green-400 font-bold">Live on Mainnet</span>
                      <span className="text-gray-400">- Real blockchain balances</span>
                    </div>
                  </div>

                  {/* Disconnect Button */}
                  <Button
                    onClick={handleDisconnect}
                    variant="outline"
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    Disconnect Wallet
                  </Button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
